/**
 * お気に入り機能
 * localStorage でお気に入り商品を管理
 */

const FAVORITES_KEY = 'valmore_favorites';
const ITEMS_PER_PAGE = 12;

// お気に入り管理クラス
class FavoritesManager {
  constructor() {
    this.favorites = this.loadFavorites();
  }

  // localStorageから読み込み
  loadFavorites() {
    try {
      const stored = localStorage.getItem(FAVORITES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to load favorites:', e);
      return [];
    }
  }

  // localStorageに保存
  saveFavorites() {
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(this.favorites));
      this.updateAllButtons();
    } catch (e) {
      console.error('Failed to save favorites:', e);
    }
  }

  // お気に入りに追加
  add(productId) {
    // 重複チェック
    if (!this.isFavorite(productId)) {
      this.favorites.unshift(productId); // 先頭に追加
      this.saveFavorites();
      return true;
    }
    return false;
  }

  // お気に入りから削除
  remove(productId) {
    const index = this.favorites.indexOf(productId);
    if (index > -1) {
      this.favorites.splice(index, 1);
      this.saveFavorites();
      return true;
    }
    return false;
  }

  // トグル（追加/削除）
  toggle(productId) {
    if (this.isFavorite(productId)) {
      this.remove(productId);
      return false;
    } else {
      this.add(productId);
      return true;
    }
  }

  // お気に入りかチェック
  isFavorite(productId) {
    return this.favorites.includes(productId);
  }

  // 全お気に入り商品IDを取得
  getAll() {
    return [...this.favorites];
  }

  // お気に入り件数
  getCount() {
    return this.favorites.length;
  }

  // 全ボタンの状態を更新
  updateAllButtons() {
    document.querySelectorAll('[data-favorite-button]').forEach(button => {
      const productId = button.dataset.productId;
      this.updateButton(button, this.isFavorite(productId));
    });
  }

  // ボタンの状態を更新
  updateButton(button, isFavorite) {
    const icon = button.querySelector('[data-favorite-icon]');
    if (icon) {
      const activeIcon = icon.dataset.activeIcon;
      const inactiveIcon = icon.dataset.inactiveIcon;
      icon.src = isFavorite ? activeIcon : inactiveIcon;
    }
    button.setAttribute('aria-pressed', isFavorite);
  }
}

// グローバルインスタンス
window.favoritesManager = new FavoritesManager();

// Shopify Storefront API でお気に入り商品を取得
async function fetchFavoriteProducts(productIds) {
  if (!productIds || productIds.length === 0) {
    return [];
  }

  const storefrontAccessToken = window.Shopify?.storefrontAccessToken;
  const shopDomain = window.Shopify?.shop;

  if (!storefrontAccessToken || !shopDomain) {
    console.error('Shopify configuration not found');
    return [];
  }

  const query = `
    query getFavoriteProducts($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          title
          handle
          featuredImage {
            url
            altText
          }
          media(first: 1) {
            edges {
              node {
                ... on MediaImage {
                  image {
                    url
                    altText
                  }
                }
                ... on Model3d {
                  previewImage {
                    url
                    altText
                  }
                }
              }
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          availableForSale
          onlinePurchaseAvailable: metafield(namespace: "custom", key: "online_purchase_available") {
            value
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${shopDomain}/api/2025-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
      },
      body: JSON.stringify({
        query,
        variables: { ids: productIds }
      })
    });

    const result = await response.json();

    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      return [];
    }

    // null を除外して商品のみ返す
    const products = (result.data?.nodes || []).filter(node => node !== null);

    // featuredImageがnullの場合、mediaから画像を取得
    return products.map(product => {
      if (!product.featuredImage && product.media?.edges?.length > 0) {
        const firstMedia = product.media.edges[0].node;
        // MediaImageの場合
        if (firstMedia.image) {
          product.featuredImage = firstMedia.image;
        }
        // Model3dの場合
        else if (firstMedia.previewImage) {
          product.featuredImage = firstMedia.previewImage;
        }
      }
      return product;
    });
  } catch (error) {
    console.error('Failed to fetch products:', error);
    return [];
  }
}

// お気に入りボタンのイベントハンドラー設定
function initializeFavoriteButtons() {
  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-favorite-button]');
    if (!button) return;

    e.preventDefault();
    const productId = button.dataset.productId;
    const isFavorite = window.favoritesManager.toggle(productId);

    // オプション: アニメーション効果
    button.classList.add('favorite-toggle');
    setTimeout(() => button.classList.remove('favorite-toggle'), 300);
  });

  // 初期状態を反映
  window.favoritesManager.updateAllButtons();
}

// DOMContentLoaded で初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFavoriteButtons);
} else {
  initializeFavoriteButtons();
}
